/**
 * The canonical elevation totals, in both directions.
 *
 * One rule, applied twice. Walking the samples in order, a move counts only once it clears
 * `threshold` metres from the current baseline — strictly greater, so an exact 6.000m rise does not
 * count. When it counts, the whole move is added and the baseline jumps to that sample. Any move in
 * the opposite direction drags the baseline with it, however small.
 *
 * Descent is that same procedure with the directions swapped. It is deliberately not a separate
 * algorithm: the mirror keeps a loop's two totals in step (they agree exactly on a noise-free loop),
 * whereas the variants that reset only on a *significant* move against the direction drift apart by
 * 7-24m on the same geometry.
 *
 * The reference implementation is `Simplifiable#calculate_total_elevation_gain` /
 * `#calculate_total_elevation_loss`, and both directions are pinned against the shared fixture in
 * `spec/fixtures/elevation/canonical_elevation_cases.json` — a byte-identical copy of which sits in
 * the iOS and Android repos.
 *
 * Why this exists rather than each caller looping: summing raw deltas instead reads ~1.28x high on
 * real geometry, because it banks DEM and GPS jitter as climb. On a jittery level towpath it invents
 * hundreds of metres of ascent that isn't there.
 */

export const ELEVATION_THRESHOLD_M = 6;

/**
 * One sample of the series. A sample can have no reading: imported geometry carries a null altitude
 * where the elevation service had no coverage, and a `[lng, lat]` coordinate with no third element
 * reads back as `undefined`. Both are the JS spelling of Ruby's `nil` here.
 */
export type AltitudeSample = number | null | undefined;

/** Shared core. `sign` is +1 for ascent, -1 for descent. */
function movingBaselineTotal(
  altitudes: AltitudeSample[],
  sign: 1 | -1,
  threshold: number
): number {
  if (!altitudes || altitudes.length < 2) return 0;

  let total = 0;
  // Unset until the first real reading, so a series that opens with a gap starts at the first
  // sample that has one rather than measuring from nothing.
  let baseline: number | null = null;

  for (let i = 0; i < altitudes.length; i++) {
    const altitude = altitudes[i];

    // No reading here: nothing moves — exactly what `moving_baseline_running` does with a nil.
    // It matters because JS coerces a null to 0 in the arithmetic below, so an uncovered sample
    // would otherwise invent a climb the size of the whole altitude, and a drop back after it.
    if (altitude === null || altitude === undefined) continue;

    if (baseline === null) {
      baseline = altitude;
      continue;
    }

    // Movement in the direction being measured, as a positive number when it goes our way.
    const move = sign * (altitude - baseline);

    if (move > threshold) {
      total += move;
      baseline = altitude; // a real move: bank it and re-seed here
    } else if (move < 0) {
      baseline = altitude; // any move the other way drags the baseline with it
    }
  }

  return total;
}

/** Total metres climbed over a bare altitude series. */
export function elevationGainFromAltitudes(
  altitudes: AltitudeSample[],
  threshold: number = ELEVATION_THRESHOLD_M
): number {
  return movingBaselineTotal(altitudes, 1, threshold);
}

/** Total metres descended over a bare altitude series, as a positive number. */
export function elevationLossFromAltitudes(
  altitudes: AltitudeSample[],
  threshold: number = ELEVATION_THRESHOLD_M
): number {
  return movingBaselineTotal(altitudes, -1, threshold);
}

/** Both totals in one pass' worth of API, for the callers that display the pair. */
export function elevationTotalsFromAltitudes(
  altitudes: AltitudeSample[],
  threshold: number = ELEVATION_THRESHOLD_M
): { ascent: number; descent: number } {
  return {
    ascent: elevationGainFromAltitudes(altitudes, threshold),
    descent: elevationLossFromAltitudes(altitudes, threshold),
  };
}

/** Coordinates are `[lng, lat, elevation?]`; only index 2 is read, and it may be absent. */
export function altitudesFromCoordinates(coordinates: number[][]): AltitudeSample[] {
  if (!coordinates) return [];
  return coordinates.map((coordinate) => coordinate[2]);
}

/** Total metres descended over a coordinate array, as a positive number. */
export function elevationLossFromCoordinates(
  coordinates: number[][],
  threshold: number = ELEVATION_THRESHOLD_M
): number {
  return elevationLossFromAltitudes(altitudesFromCoordinates(coordinates), threshold);
}
